import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { PublicLayout } from '@/components/layout'
import { PageTransition } from '@/components/page-transition'
import { usePricingData } from '@/features/pricing/hooks'
import { api } from '@/lib/api'
import { getLobeIcon } from '@/lib/lobe-icon'
import { cn } from '@/lib/utils'

import {
  sortByHeat,
  toPlazaModel,
  type PlazaKind,
  type PlazaModel,
  type PlazaPerf,
} from './lib/plaza'

const BACKGROUND_STYLE = {
  background: [
    'radial-gradient(ellipse 60% 50% at 20% 20%, oklch(0.72 0.18 250 / 80%) 0%, transparent 70%)',
    'radial-gradient(ellipse 50% 40% at 80% 15%, oklch(0.65 0.15 200 / 60%) 0%, transparent 70%)',
    'radial-gradient(ellipse 40% 35% at 40% 80%, oklch(0.70 0.12 280 / 40%) 0%, transparent 70%)',
  ].join(', '),
}

type PerfMetrics = Record<string, PlazaPerf>

function usePerfMetrics(): PerfMetrics {
  const { data } = useQuery({
    queryKey: ['plaza-perf-metrics'],
    queryFn: async () => {
      const res = await api.get('/api/perf-metrics/summary?hours=24')
      return (res.data?.data?.models ?? []) as {
        model_name: string
        avg_latency_ms: number
        avg_tps: number
        success_rate: number
      }[]
    },
    staleTime: 5 * 60 * 1000,
  })

  return useMemo(() => {
    const map: PerfMetrics = {}
    for (const item of data ?? []) {
      map[item.model_name] = {
        latencyMs: item.avg_latency_ms,
        tps: item.avg_tps,
        successRate: item.success_rate,
      }
    }
    return map
  }, [data])
}

function money(value: number): string {
  if (Number.isInteger(value)) return `¥${value}`
  return `¥${value < 0.01 ? value.toFixed(3) : value.toFixed(2)}`
}

function Segment<T extends string>(props: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div className='bg-muted flex gap-1 rounded-xl p-1'>
      {props.options.map((option) => (
        <button
          key={option.value}
          type='button'
          onClick={() => props.onChange(option.value)}
          className={cn(
            'rounded-lg px-3 py-1.5 text-[13px] transition',
            props.value === option.value
              ? 'bg-card text-foreground font-medium shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function ModelCard(props: {
  model: PlazaModel
  perf?: PlazaPerf
  onSelect: () => void
}) {
  const { model, perf } = props
  let discountLabel = '限时优惠'
  if (model.free) {
    discountLabel = '免费'
  } else if (model.discount) {
    discountLabel = `官方 ${model.discount.toFixed(1)} 折`
  }
  let badgeClass = 'bg-muted text-muted-foreground'
  if (model.free) {
    badgeClass = 'bg-success/15 text-success'
  } else if (model.discount) {
    badgeClass = 'text-primary-foreground bg-gradient-to-r from-destructive to-warning'
  }

  return (
    <button
      type='button'
      onClick={props.onSelect}
      className={cn(
        'group bg-card border-border relative flex cursor-pointer flex-col gap-3 rounded-2xl border p-4 text-left shadow-sm transition duration-200',
        'hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_0_0_1px_color-mix(in_oklab,var(--warning)_30%,transparent),0_18px_40px_-22px_color-mix(in_oklab,var(--destructive)_55%,transparent)]',
        'hover:bg-[linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(130deg,var(--destructive),var(--warning),var(--primary))_border-box]',
        'focus-visible:border-transparent focus-visible:outline-none focus-visible:bg-[linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(130deg,var(--destructive),var(--warning),var(--primary))_border-box]'
      )}
    >
      <span className='flex items-center gap-2.5'>
        <span className='bg-muted flex size-9 shrink-0 items-center justify-center rounded-xl'>
          {getLobeIcon(model.icon, 22)}
        </span>
        <span className='min-w-0 flex-1'>
          <span className='block truncate font-medium'>{model.name}</span>
          <span className='text-muted-foreground block text-xs'>
            {model.vendor}
          </span>
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold',
            badgeClass
          )}
        >
          {discountLabel}
        </span>
      </span>

      {model.kind === 'image' ? (
        <>
          <span className='flex items-baseline gap-1.5'>
            <span className='text-2xl font-semibold tracking-tight'>
              {money(model.tiers?.[0]?.price ?? 0)}
            </span>
            <span className='text-muted-foreground text-xs'>
              / 张{model.tiers?.[0] ? `（${model.tiers[0].label}）` : ''}
            </span>
          </span>
          <span className='bg-muted grid grid-cols-3 gap-2 rounded-xl p-2.5'>
            {(model.tiers ?? []).slice(0, 3).map((tier) => (
              <span key={tier.label} className='flex flex-col'>
                <span className='text-muted-foreground text-[11px]'>
                  {tier.label}
                </span>
                <span className='text-[13px] font-medium'>
                  {money(tier.price)}
                </span>
              </span>
            ))}
          </span>
        </>
      ) : (
        <>
          <span className='flex items-baseline gap-1.5'>
            <span className='text-2xl font-semibold tracking-tight'>
              {money(model.inputPrice ?? 0)}
            </span>
            <span className='text-muted-foreground text-xs'>
              / 百万输入 tokens
            </span>
          </span>
          <span className='bg-muted grid grid-cols-3 gap-2 rounded-xl p-2.5'>
            <span className='flex flex-col'>
              <span className='text-muted-foreground text-[11px]'>缓存命中</span>
              <span className='text-[13px] font-medium'>
                {model.cachePrice ? money(model.cachePrice) : '—'}
              </span>
            </span>
            <span className='flex flex-col'>
              <span className='text-muted-foreground text-[11px]'>输出</span>
              <span className='text-[13px] font-medium'>
                {model.outputPrice ? money(model.outputPrice) : '—'}
              </span>
            </span>
            <span className='flex flex-col'>
              <span className='text-muted-foreground text-[11px]'>延迟</span>
              <span className='text-[13px] font-medium'>
                {perf ? `${(perf.latencyMs / 1000).toFixed(1)}s` : '—'}
              </span>
            </span>
          </span>
        </>
      )}

      <span className='flex flex-wrap gap-1.5'>
        {model.context && (
          <span className='bg-accent text-accent-foreground rounded-full px-2.5 py-0.5 text-[11.5px] font-medium'>
            {model.context} 上下文
          </span>
        )}
        {model.capabilities.slice(0, 3).map((capability) => (
          <span
            key={capability}
            className='bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-[11.5px]'
          >
            {capability}
          </span>
        ))}
        {model.protocols.map((protocol) => (
          <span
            key={protocol.id}
            className='border-border text-muted-foreground rounded-full border px-2.5 py-0.5 text-[11.5px]'
          >
            {protocol.label}
          </span>
        ))}
      </span>
    </button>
  )
}

function DetailPanel(props: {
  model: PlazaModel | null
  perf?: PlazaPerf
  onClose: () => void
}) {
  const { model, perf } = props
  const open = model !== null
  return (
    <>
      <div
        onClick={props.onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/45 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      />
      <aside
        className={cn(
          'bg-card border-border fixed inset-y-0 right-0 z-50 w-[min(520px,94vw)] overflow-y-auto border-l shadow-2xl transition-transform duration-200',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {model && (
          <>
            <div className='border-border bg-card sticky top-0 flex items-start justify-between gap-3 border-b p-5'>
              <div className='flex items-center gap-3'>
                <span className='bg-muted flex size-10 items-center justify-center rounded-xl'>
                  {getLobeIcon(model.icon, 24)}
                </span>
                <div>
                  <div className='font-semibold'>{model.name}</div>
                  <div className='text-muted-foreground text-xs'>
                    {model.vendor}
                    {model.context ? ` · ${model.context} 上下文` : ''}
                  </div>
                </div>
              </div>
              <button
                type='button'
                onClick={props.onClose}
                className='bg-muted text-muted-foreground hover:text-foreground size-8 cursor-pointer rounded-lg'
              >
                ✕
              </button>
            </div>

            <div className='flex flex-col gap-6 p-5'>
              <section>
                <h4 className='text-muted-foreground mb-2.5 text-xs font-semibold tracking-wide'>
                  价格
                </h4>
                <dl className='grid grid-cols-2 gap-y-2 text-sm'>
                  {model.kind === 'image'
                    ? (model.tiers ?? []).map((tier) => (
                        <DetailRow key={tier.label} label={tier.label}>
                          {money(tier.price)} / 张
                        </DetailRow>
                      ))
                    : [
                        ['输入', `${money(model.inputPrice ?? 0)} / 百万`],
                        [
                          '缓存命中',
                          model.cachePrice
                            ? `${money(model.cachePrice)} / 百万`
                            : '—',
                        ],
                        [
                          '输出',
                          model.outputPrice
                            ? `${money(model.outputPrice)} / 百万`
                            : '—',
                        ],
                      ].map(([label, value]) => (
                        <DetailRow key={label} label={label}>
                          {value}
                        </DetailRow>
                      ))}
                  {model.discount && (
                    <DetailRow label='折扣'>
                      官方 {model.discount.toFixed(1)} 折
                    </DetailRow>
                  )}
                </dl>
              </section>

              {perf && (
                <section>
                  <h4 className='text-muted-foreground mb-2.5 text-xs font-semibold tracking-wide'>
                    性能
                  </h4>
                  <dl className='grid grid-cols-2 gap-y-2 text-sm'>
                    <DetailRow label='平均延迟'>{perf.latencyMs} ms</DetailRow>
                    <DetailRow label='平均 TPS'>{perf.tps}</DetailRow>
                    <DetailRow label='成功率'>{perf.successRate}%</DetailRow>
                  </dl>
                </section>
              )}

              <section>
                <h4 className='text-muted-foreground mb-2.5 text-xs font-semibold tracking-wide'>
                  支持的协议
                </h4>
                <div className='flex flex-wrap gap-1.5'>
                  {model.protocols.map((protocol) => (
                    <span
                      key={protocol.id}
                      className='border-border rounded-full border px-2.5 py-0.5 text-xs'
                    >
                      {protocol.label}
                    </span>
                  ))}
                </div>
              </section>

              <button
                type='button'
                onClick={() => void navigator.clipboard?.writeText(model.name)}
                className='bg-foreground text-background w-full cursor-pointer rounded-xl py-3 text-sm font-medium'
              >
                复制模型名
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  )
}

function DetailRow(props: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className='text-muted-foreground'>{props.label}</dt>
      <dd className='text-right font-medium'>{props.children}</dd>
    </>
  )
}

export function ModelPlaza() {
  const { models, groupRatio, isLoading } = usePricingData()
  const perf = usePerfMetrics()
  const [kind, setKind] = useState<PlazaKind | 'all'>('all')
  const [pay, setPay] = useState<'all' | 'free'>('all')
  const [selected, setSelected] = useState<string | null>(null)

  const plazaModels = useMemo(() => {
    const defaultRatio = groupRatio?.default ?? 1
    return sortByHeat(
      models.map((model) => toPlazaModel(model, defaultRatio))
    )
  }, [models, groupRatio])

  const visible = plazaModels.filter(
    (model) =>
      (kind === 'all' || model.kind === kind) &&
      (pay === 'all' || model.free)
  )

  const selectedModel =
    plazaModels.find((model) => model.name === selected) ?? null

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelected(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <PublicLayout showMainContainer={false}>
      <div className='relative'>
        <div
          aria-hidden
          className='pointer-events-none absolute inset-x-0 top-0 h-[600px] opacity-25 dark:opacity-[0.12]'
          style={{
            ...BACKGROUND_STYLE,
            maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, black 40%, transparent 100%)',
          }}
        />
        <div
          aria-hidden
          className='border-border pointer-events-none absolute inset-x-0 top-0 h-[600px] opacity-[0.08]'
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)',
            backgroundSize: '4rem 4rem',
            maskImage:
              'radial-gradient(ellipse 60% 50% at 50% 30%, black 20%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 60% 50% at 50% 30%, black 20%, transparent 100%)',
          }}
        />

      <PageTransition className='relative mx-auto w-full max-w-[1280px] px-3 pt-16 pb-10 sm:px-6 sm:pt-20 sm:pb-12 xl:px-8'>
        <h1 className='mb-5 text-2xl font-semibold tracking-tight'>模型广场</h1>

        <div className='mb-5 flex flex-wrap items-center gap-3'>
          <Segment
            value={kind}
            onChange={setKind}
            options={[
              { value: 'all', label: '全部' },
              { value: 'text', label: '文本' },
              { value: 'image', label: '生图' },
              { value: 'video', label: '视频' },
            ]}
          />
          <Segment
            value={pay}
            onChange={setPay}
            options={[
              { value: 'all', label: '付费 + 免费' },
              { value: 'free', label: '免费' },
            ]}
          />
        </div>

        {isLoading && (
          <div className='text-muted-foreground py-16 text-center text-sm'>
            加载中…
          </div>
        )}
        {!isLoading && visible.length === 0 && (
          <div className='text-muted-foreground py-16 text-center text-sm'>
            该分类下暂无模型
          </div>
        )}
        {!isLoading && visible.length > 0 && (
          <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {visible.map((model) => (
              <ModelCard
                key={model.name}
                model={model}
                perf={perf[model.name]}
                onSelect={() => setSelected(model.name)}
              />
            ))}
          </div>
        )}
      </PageTransition>
      </div>

      <DetailPanel
        model={selectedModel}
        perf={selectedModel ? perf[selectedModel.name] : undefined}
        onClose={() => setSelected(null)}
      />
    </PublicLayout>
  )
}
